// src/auth/components/ProtectedButton.jsx
import React from 'react';
import { hasPermission, hasAnyPermission, hasAllPermissions } from './authUtils';

/**
 * ProtectedButton Component
 * 
 * Renders a button only if user has required permission(s).
 * Supports single permission, any of multiple, or all of multiple.
 * 
 * @param {string} permission - Single permission code
 * @param {string[]} anyPermissions - Array of permissions (user needs ANY)
 * @param {string[]} allPermissions - Array of permissions (user needs ALL)
 * @param {React.ReactNode} children - Button content
 * @param {function} onClick - Click handler
 * @param {string} className - Additional CSS classes
 * @param {boolean} disabled - Disabled state
 * @param {string} variant - Button variant (primary, secondary, danger, success)
 * @param {string} size - Button size (sm, md, lg)
 * @param {React.ReactNode} icon - Icon component
 * @param {string} type - Button type (button, submit, reset)
 * @param {object} ...props - Additional props passed to button
 * 
 * @example
 * // Single permission
 * <ProtectedButton 
 *   permission={PERMISSIONS.USER_CREATE}
 *   onClick={handleCreate}
 * >
 *   Add User
 * </ProtectedButton>
 * 
 * @example
 * // Any of multiple permissions
 * <ProtectedButton 
 *   anyPermissions={[PERMISSIONS.USER_CREATE, PERMISSIONS.USER_UPDATE]}
 *   onClick={handleEdit}
 *   variant="primary"
 * >
 *   Edit
 * </ProtectedButton>
 * 
 * @example
 * // All of multiple permissions
 * <ProtectedButton 
 *   allPermissions={[PERMISSIONS.BUDGET_UPDATE, PERMISSIONS.BUDGET_APPROVE]}
 *   onClick={handleApprove}
 *   variant="success"
 * >
 *   Approve Budget
 * </ProtectedButton>
 */
const ProtectedButton = ({
  permission,
  anyPermissions,
  allPermissions,
  children,
  onClick,
  className = '',
  disabled = false,
  variant = 'primary',
  size = 'md',
  icon,
  type = 'button',
  ...props
}) => {
  // Check permissions
  let hasAccess = false;

  if (permission) {
    // Single permission check
    hasAccess = hasPermission(permission);
  } else if (anyPermissions && anyPermissions.length > 0) {
    // Any of multiple permissions
    hasAccess = hasAnyPermission(anyPermissions);
  } else if (allPermissions && allPermissions.length > 0) {
    // All of multiple permissions
    hasAccess = hasAllPermissions(allPermissions);
  } else {
    // No permission specified - show by default
    hasAccess = true;
  }

  // Don't render if no access
  if (!hasAccess) {
    return null;
  }

  // Base button classes
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-lg transition-all focus:outline-none focus:ring-2 focus:ring-offset-2';

  // Variant classes
  const variantClasses = {
    primary: 'bg-blue-600 hover:bg-blue-700 text-white focus:ring-blue-500 disabled:bg-blue-300',
    secondary: 'bg-gray-600 hover:bg-gray-700 text-white focus:ring-gray-500 disabled:bg-gray-300',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500 disabled:bg-red-300',
    success: 'bg-green-600 hover:bg-green-700 text-white focus:ring-green-500 disabled:bg-green-300',
    warning: 'bg-yellow-600 hover:bg-yellow-700 text-white focus:ring-yellow-500 disabled:bg-yellow-300',
    info: 'bg-cyan-600 hover:bg-cyan-700 text-white focus:ring-cyan-500 disabled:bg-cyan-300',
    outline: 'border-2 border-blue-600 text-blue-600 hover:bg-blue-50 focus:ring-blue-500 disabled:border-blue-300 disabled:text-blue-300',
  };

  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm gap-1.5',
    md: 'px-4 py-2 text-base gap-2',
    lg: 'px-6 py-3 text-lg gap-2.5',
  };

  // Combine classes
  const buttonClasses = `
    ${baseClasses}
    ${variantClasses[variant] || variantClasses.primary}
    ${sizeClasses[size] || sizeClasses.md}
    ${disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
    ${className}
  `.trim().replace(/\s+/g, ' ');

  return (
    <button
      type={type}
      onClick={onClick}
      className={buttonClasses}
      disabled={disabled}
      {...props}
    >
      {icon && <span className="flex-shrink-0">{icon}</span>}
      {children}
    </button>
  );
};

export default ProtectedButton;